import SimpleSort from '@Components/Common/SimpleSort';
import InfiniteScroll from '@Components/General/InfiniteScroll';
import SelectionDialog from '@Components/General/SelectionDialog';
import Toolbar from '@Components/General/Toolbar';
import useDefaultInfiniteQuery from '@hooks/useDefaultInfiniteQuery';
import useStateSimpleSort from '@hooks/useStateSimpleSort';
import { Button, Card, CardHeader, Typography } from '@mui/material';
import { emailTemplatesQueryOptions } from '@utils/queryOptions';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TemplateCard from '../template-card';
import { SpaceDashboardRounded } from '@mui/icons-material';
import TemplateList from './template-list';
import GridBox from '@Components/General/grid-box';

interface TemplateSelectProps {
    open: boolean;
    onClose: () => void;
    onSelect: (template: any) => void;
    standalone?: boolean;
    actionText: string;
    template?: any;
}

const TemplateSelect = ({
    open,
    onClose,
    template,
    onSelect,
    standalone,
    actionText = 'Select',
}: TemplateSelectProps) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');

    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const simpleSortHook = useStateSimpleSort({
        inverseFields: ['created', 'updated'],
        defaultSortField: 'title',
    });

    const params = { search, ordering: simpleSortHook.ordering };

    const {
        data: { flatData: templates, count } = {},
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
    } = useDefaultInfiniteQuery(emailTemplatesQueryOptions(params));

    if (isLoading) {
        return null;
    }

    return (
        <SelectionDialog
            open={open}
            onClose={onClose}
            title={
                <>
                    {actionText} <strong>{template?.parent_title}</strong>
                </>
            }
            searchProps={{
                searchPlacholder: t('Search Templates'),
                onSubmit: setSearch,
            }}
            toolbar={
                <Toolbar>
                    <Typography variant="titleMedium">
                        {count} {t('Templates')}
                    </Typography>
                    <SimpleSort
                        sortOptions={[
                            { value: 'title', label: t('Title') },
                            {
                                value: 'created',
                                label: t('Last Created'),
                            },
                            {
                                value: 'updated',
                                label: t('Last Updated'),
                            },
                        ]}
                        {...simpleSortHook}
                    />
                </Toolbar>
            }
            actions={
                <>
                    <Button variant="text" onClick={onClose}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={() => onSelect(selectedTemplate)}
                        disabled={!selectedTemplate?.id}
                    >
                        {actionText}
                    </Button>
                </>
            }
        >
            {standalone && (
                <Card
                    variant="filled"
                    onClick={() => setSelectedTemplate(template)}
                    onDoubleClick={e => onSelect?.(template, e)}
                    data-active={
                        selectedTemplate?.id === template?.id ? '' : undefined
                    }
                    sx={{
                        cursor: 'pointer',
                        mb: 2,
                        width: 196,
                        '&[data-active]': {
                            backgroundColor: 'secondaryContainer.main',
                            '&:hover': {
                                backgroundColor: 'action.selectedHover',
                            },
                        },
                    }}
                >
                    <CardHeader
                        avatar={
                            <SpaceDashboardRounded
                                fontSize="large"
                                color="action"
                            />
                        }
                        title={t('Standalone Template')}
                        titleTypographyProps={{ variant: 'titleMedium' }}
                    />
                </Card>
            )}
            <InfiniteScroll
                ListComponent={GridBox}
                loadMore={fetchNextPage}
                loading={isFetchingNextPage}
            >
                <TemplateList
                    templates={templates}
                    onSelect={setSelectedTemplate}
                    selectedTemplate={selectedTemplate}
                    onTemplateClick={setSelectedTemplate}
                    actionText={actionText}
                />
            </InfiniteScroll>
        </SelectionDialog>
    );
};

export default TemplateSelect;
